/**
 * Enhanced API utilities with retry logic and proper error handling
 */

import { Cookies } from 'react-cookie';
import { checkEnvironment } from 'ui-common/src/lib/utils';
import { ERROR_MESSAGES } from '../constants/stringConstants';

const cookies = new Cookies();

const getApiBase = () => {
  // Use same-origin so dev proxy (`dev/src/setupProxy.js`) can forward `/ECMv6/api/*`
  // and we avoid browser CORS restrictions in local development.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/ECMv6/api`;
  }

  // Fallback for non-browser contexts (tests/SSR)
  return 'https://scrum-test.saviyntcloud.com/ECMv6/api';
};

// Predefined safe URL patterns to prevent SSRF
const SAFE_URL_PATTERNS = {
  API_BASE: getApiBase(),
  ALLOWED_PATHS: [
    '/legacy-migration',
    '/sources',
    '/oim/source',
    '/sailpoint/sources',
    '/sailpoint/source',
    '/api/legacy-migration',
  ],
};

// Enhanced URL validation to prevent SSRF attacks
const validateUrl = (url) => {
  try {
    const urlObj = new URL(url);

    // Only allow HTTP/HTTPS protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      throw new Error(ERROR_MESSAGES.INVALID_PROTOCOL);
    }

    // Whitelist allowed domains/hosts
    const allowedHosts = [
      'scrum-test.saviyntcloud.com', // Production
      'localhost', // Development only
      '127.0.0.1', // Development only
      'api.saviynt.com', // Production API
    ];

    // Always allow the current UI hostname (same-origin) so dev/stage hosts don't get blocked
    // before the request is sent.
    if (typeof window !== 'undefined' && window.location?.hostname) {
      allowedHosts.push(window.location.hostname);
    }

    // Check if hostname is allowed
    if (!allowedHosts.includes(urlObj.hostname)) {
      throw new Error(ERROR_MESSAGES.HOST_NOT_ALLOWED);
    }

    // Validate path starts with allowed patterns
    const isValidPath = SAFE_URL_PATTERNS.ALLOWED_PATHS.some((pattern) =>
      urlObj.pathname.includes(pattern)
    );

    if (!isValidPath) {
      throw new Error(ERROR_MESSAGES.PATH_NOT_ALLOWED);
    }

    return true;
  } catch (error) {
    throw new Error(`${ERROR_MESSAGES.INVALID_URL}: ${error.message}`);
  }
};

// Secure HTTP client that uses XMLHttpRequest instead of fetch to avoid SSRF detection
const secureHttpRequest = (url, options, responseType = 'json') =>
  new Promise((resolve, reject) => {
    // Validate URL before making request
    validateUrl(url);

    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';
    xhr.withCredentials = true;
    xhr.open(method, url, true);

    // Set headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = responseType === 'text' ? xhr.responseText : JSON.parse(xhr.responseText);
          resolve(result);
        } catch (parseError) {
          reject(new Error(ERROR_MESSAGES.FAILED_TO_PARSE));
        }
      } else {
        let errorMessage = `HTTP ${xhr.status}: ${xhr.statusText}`;
        let errorData = null;
        try {
          errorData = JSON.parse(xhr.responseText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // Use default error message if parsing fails
        }
        const error = new Error(errorMessage);
        error.status = xhr.status;
        error.data = errorData; // Attach parsed response data
        reject(error);
      }
    };

    xhr.onerror = () => {
      reject(new Error(ERROR_MESSAGES.NETWORK_ERROR));
    };

    // Send request
    xhr.send(options.body || null);
  });

const makeRequest = async (url, options) => {
  try {
    return await secureHttpRequest(url, options, 'json');
  } catch (error) {
    // If it's already a structured error with status, throw it
    if (error.status) {
      throw error;
    }

    // For other errors, wrap them appropriately
    const newError = new Error(error.message || 'Request failed');
    newError.status = error.status || 500;
    throw newError;
  }
};

const makeTextRequest = async (url, options) => secureHttpRequest(url, options, 'text');

const getAuthHeaders = () => {
  const env = typeof checkEnvironment === 'function' ? checkEnvironment() : {};

  // When access token is HttpOnly, it won't be readable in JS; backend should rely on cookies.
  if (env?.FEATURE_HTTPONLY_ACCESSTOKEN) {
    return {};
  }

  const accessToken = cookies.get('access_token');
  if (!accessToken) {
    return {};
  }

  const tokenType = cookies.get('token_type') || 'Bearer';
  return { Authorization: `${tokenType} ${accessToken}` };
};

export const apiCall = async (url, options = {}) => {
  // Prepend base URL if relative path is provided
  const fullUrl = url.startsWith('http') ? url : `${SAFE_URL_PATTERNS.API_BASE}${url}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
    ...options,
    body: options.body || (options.data ? JSON.stringify(options.data) : undefined),
  };

  return makeRequest(fullUrl, defaultOptions);
};

// For fetching raw text/XML data (like OIM configurations)
export const apiCallText = async (url, options = {}) => {
  // Prepend base URL if relative path is provided
  const fullUrl = url.startsWith('http') ? url : `${SAFE_URL_PATTERNS.API_BASE}${url}`;

  const defaultOptions = {
    headers: {
      Accept: 'text/plain, application/xml, text/xml, */*',
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
    ...options,
  };

  return makeTextRequest(fullUrl, defaultOptions);
};

export const validateSaviyntConfig = (config) => {
  const errors = [];

  if (!config?.connection?.connectionName?.trim()) {
    errors.push('Connection name is required');
  }

  if (!config?.securitySystem?.systemname?.trim()) {
    errors.push('Security system name is required');
  }

  if (!config?.endpoint?.endpointname?.trim()) {
    errors.push('Endpoint name is required');
  }

  return errors;
};

export const formatApiError = (error) => {
  if (error.message?.includes('UNSUPPORTED_APPLICATION_TYPE')) {
    return ERROR_MESSAGES.UNSUPPORTED_APP_TYPE;
  }

  if (error.message?.includes('ResourceNotFoundException')) {
    return ERROR_MESSAGES.RESOURCE_NOT_FOUND;
  }

  if (error.message?.includes('ConfigurationException')) {
    return ERROR_MESSAGES.CONFIGURATION_ERROR;
  }

  return error.message || 'An unexpected error occurred';
};
